import React, { useEffect, useState } from 'react';
import { BellRing } from 'lucide-react';
import { tt } from '@/utils/localizedStrings';
import { AUDIO_STRINGS } from '@/utils/audioStrings';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import { pushSupport, isPushEnabled, enablePush, disablePush, getPushState } from '@/utils/prayerPush';
import { PushTestButton, pushText } from '@/components/PushTestButton';

const permissionGranted = () => typeof Notification !== 'undefined' && Notification.permission === 'granted';

export const BackgroundPushSection = ({ lang, isRTL, pushParams, P }) => {
  const support = pushSupport();
  const [on, setOn] = useState(() => support === 'ok' && isPushEnabled() && permissionGranted());
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(support === 'unsupported' ? 'pushUnsupported' : null);

  // Read the real state from the phone: it may drop the permission after it was granted.
  useEffect(() => {
    let alive = true;
    getPushState().then((s) => {
      if (!alive) return;
      setOn(s === 'on' || s === 'dropped');
      if (s === 'lost') setMsg('pushLost');
      if (s === 'dropped') setMsg('pushDropped');
    });
    return () => { alive = false; };
  }, []);

  const toggle = async () => {
    if (busy) return;
    setMsg(null);
    if (on) {
      setBusy(true);
      await disablePush();
      setOn(false);
      setBusy(false);
      return;
    }
    if (support === 'unsupported') { setMsg('pushUnsupported'); return; }
    if (!pushParams) { setMsg('pushNeedLocation'); return; }
    setBusy(true);
    try {
      await enablePush(pushParams);
      setOn(true);
    } catch (e) {
      if (e.message === 'denied' || Notification.permission === 'denied') setMsg('pushDenied');
      else if (e.message !== 'dismissed') setMsg('pushError');
    }
    setBusy(false);
  };

  const hint = { color: P.muted, fontSize: 12, lineHeight: 1.6 };
  return (
    <div
      className="rounded-xl p-3 mb-5"
      style={{ background: on ? 'rgba(16,185,129,0.10)' : P.field, border: `1px solid ${on ? 'rgba(52,211,153,0.55)' : P.border}` }}
      data-testid="audio-push-section"
    >
      <div className="flex items-start gap-3">
        <BellRing size={20} color={P.accent} className="mt-0.5 shrink-0" />
        <div className="flex-1">
          <div style={{ color: P.primary, fontSize: 14.5, fontWeight: 700 }}>{tt(AUDIO_STRINGS.pushTitle, lang)}</div>
          <div style={{ ...hint, color: P.secondary, marginTop: 2 }}>{tt(AUDIO_STRINGS.pushDesc, lang)}</div>
        </div>
        <ToggleSwitch
          checked={on}
          onChange={toggle}
          isRTL={isRTL}
          disabled={busy}
          testId="audio-push-toggle"
          label={tt(AUDIO_STRINGS.pushTitle, lang)}
        />
      </div>
      {msg && (
        <div className="mt-2" style={{ color: ['pushLost', 'pushDropped'].includes(msg) ? '#FCD34D' : '#FCA5A5', fontSize: 12.5, lineHeight: 1.6 }} data-testid="audio-push-message">
          {pushText(msg, lang)}
        </div>
      )}
      {on && (
        <div className="mt-2 space-y-1" data-testid="audio-push-on-hints">
          <div style={hint}>{tt(AUDIO_STRINGS.pushSoundHint, lang)}</div>
          <div style={hint}>{tt(AUDIO_STRINGS.pushRenewNote, lang)}</div>
          <PushTestButton lang={lang} P={P} pushParams={pushParams} onStart={() => setMsg(null)} />
        </div>
      )}
    </div>
  );
};
